import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '@bem-react/classname';
import { AppSelect, AppTreeSelect } from '@frontend-modules/ui-kit';
import {
    disabledDate,
    disabledHours,
    disabledMinutes,
    getTaskDateInitialValues,
    getTimeDisableValue,
    isCorrectInterval,
    isDateTimeInInterval,
    TaskDatePicker,
    TaskTimePicker,
} from '@fsd-features/task';
import { EAssignedTypes, EDeadlineTypes, EDurationTypes, TTaskFormValue } from '@fsd-shared/models/task';
import { Dayjs } from 'dayjs';
import { useRole } from 'hooks';
import { useAppSelector } from 'store/store';

import { ITaskFormProps } from './TaskForm.types';

import './TaskForm.style.scss';

const CnTaskForm = cn('task-form');
export const TaskForm: React.FC<ITaskFormProps> = (props) => {
    const { interval, periodsData, isTestLesson, nextOnlineLessonsList, onFieldsSelected } = props;

    const { teachersList } = useAppSelector((store) => store.assignemnt);

    const [calendarDurationType, setCalendarDurationType] = useState<EDurationTypes>(EDurationTypes.UNLIMITED);
    const [taskValues, setTaskValues] = useState<TTaskFormValue>({
        assignedType: undefined,
        deadlineType: undefined,
        timeToComplete: undefined, //время на выполнение
        assignedDelay: undefined, // начнут через н времени от начала занятия
        deadlineDelay: undefined, // закончат через н времени после выдачи
        assignedDate: undefined, //начнут в конкретное время
        deadlineForComplete: undefined, //закончат в конкретное время
    });

    const { isPrincipal, isMethodist, isProctor } = useRole();

    const showAssignedTimeSelector =
        !!taskValues.assignedType &&
        (taskValues.assignedType === EAssignedTypes.DATE_TIME || taskValues.assignedType === EAssignedTypes.TIME);

    const showDeadlineTimeSelector =
        !!taskValues.deadlineType &&
        (taskValues.deadlineType === EDeadlineTypes.DATE_TIME || taskValues.deadlineType === EDeadlineTypes.TIME);
    const options = getTaskDateInitialValues({ isTestLesson, nextOnlineLessonsList });

    const teachersListNormalized = useMemo(() => {
        return teachersList.length
            ? teachersList
                  .map((item) => {
                      return {
                          value: item.id,
                          label: `${item.lastName} ${item.firstName} ${item.patronymic}`,
                      };
                  })
                  .sort((a, b) => (a.label > b.label ? 1 : -1))
            : [];
    }, [teachersList]);
    const taskValuesErrors: { [key: string]: 'error' | undefined } = useMemo(() => {
        const {
            assignedType,
            assignedDate,
            assignedDelay,
            deadlineType,
            deadlineDelay,
            deadlineForComplete,
            timeToComplete,
        } = taskValues;

        const [isStartNow, isNeedStartDate, isNeedStartDelay] = [
            assignedType === EAssignedTypes.NOW,
            assignedType === EAssignedTypes.DATE_TIME,
            assignedType === EAssignedTypes.TIME,
        ];
        const [isNeedDeadlineDelay, isNeedDeadlineDate] = [
            deadlineType === EDeadlineTypes.TIME,
            deadlineType === EDeadlineTypes.DATE_TIME,
        ];

        const isCorrectIntervalSet = isCorrectInterval(assignedDate, deadlineForComplete);
        const isCorrectStart = isDateTimeInInterval(assignedDate, interval, periodsData) && isCorrectIntervalSet;
        const isCorrectEnd = isDateTimeInInterval(deadlineForComplete, interval, periodsData) && isCorrectIntervalSet;

        return {
            assignedDelay: !isStartNow && isNeedStartDelay && !assignedDelay ? 'error' : undefined,
            assignedDate: !isCorrectStart || (!isStartNow && isNeedStartDate && !assignedDate) ? 'error' : undefined,
            deadlineDelay: isNeedDeadlineDelay && !deadlineDelay ? 'error' : undefined,
            deadlineForComplete: isNeedDeadlineDate && (!deadlineForComplete || !isCorrectEnd) ? 'error' : undefined,
            timeToComplete:
                calendarDurationType === EDurationTypes.TIME_LIMITED && !timeToComplete ? 'error' : undefined,
        };
    }, [taskValues, calendarDurationType]);

    const handleDropdownClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const dropdownOptions = e.currentTarget?.querySelector('.select-suggest_focus');

        if (dropdownOptions) {
            dropdownOptions.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, []);

    const handleTaskTimeTypeChanged = (
        type: 'start' | 'end' | 'duration',
        val: EAssignedTypes | EDeadlineTypes | EDurationTypes,
    ) => {
        switch (type) {
            case 'start':
                setTaskValues((prevState) => {
                    return {
                        ...prevState,
                        assignedType: val as EAssignedTypes,
                    };
                });
                onTaskFieldSelect('assignedDelay', undefined);
                onTaskFieldSelect('assignedDate', undefined);
                break;
            case 'end':
                setTaskValues((prevState) => {
                    return {
                        ...prevState,
                        deadlineType: val as EDeadlineTypes,
                    };
                });
                onTaskFieldSelect('deadlineForComplete', undefined);
                onTaskFieldSelect('deadlineDelay', undefined);
                onTaskFieldSelect('deadlineLesson', undefined);
                break;
            default:
            case 'duration':
                setCalendarDurationType(val as EDurationTypes);
                onTaskFieldSelect('timeToComplete', val === EDurationTypes.TIME_LIMITED ? 0 : undefined);
                break;
        }
    };
    const handleTeacherSelect = (teacher: { value: string | number; label: ReactNode }) => {
        onTaskFieldSelect('teacher', teacher.value ? +teacher.value : 0);
    };

    const disabledTime = (date?: Dayjs, time?: string) => {
        const disableValues = getTimeDisableValue(date, interval, periodsData);

        return {
            disabledHours: () => disabledHours(time, disableValues),
            disabledMinutes: (hour: number) => disabledMinutes(hour, time, disableValues),
        };
    };

    const onTaskFieldSelect = (key: keyof TTaskFormValue, value: number | string | undefined) => {
        setTaskValues((prevState) => {
            return {
                ...prevState,
                [key]: value,
            };
        });
    };

    useEffect(() => {
        const hasErrors = Object.values(taskValuesErrors).some((item) => item === 'error');
        onFieldsSelected?.(taskValues, hasErrors);
    }, [taskValues, taskValuesErrors]);

    return (
        <div className={CnTaskForm()}>
            <div className={CnTaskForm('form-block')} onClick={handleDropdownClick}>
                <AppSelect
                    label={'Ученики начнут'}
                    options={options.dateStartList}
                    placeholder={'Выберите тип выдачи'}
                    value={options.dateStartList.find(({ value }) => value === taskValues.assignedType)}
                    onChange={(_, value) => {
                        handleTaskTimeTypeChanged('start', value as EAssignedTypes);
                    }}
                />
                {showAssignedTimeSelector &&
                    (taskValues.assignedType === EAssignedTypes.TIME ? (
                        <TaskTimePicker
                            placeholder={'Через'}
                            status={taskValuesErrors?.assignedDelay}
                            value={taskValues.assignedDelay}
                            onTimeSelect={(val) => onTaskFieldSelect('assignedDelay', val)}
                            nullTimeText={'В начале занятия'}
                        />
                    ) : (
                        <TaskDatePicker
                            placeholder={'Выберите время'}
                            value={taskValues.assignedDate}
                            disabledDate={(date) => disabledDate(date, interval, periodsData)}
                            disabledTime={(date) => disabledTime(date, taskValues.assignedDate)}
                            handleDateChange={(val) => onTaskFieldSelect('assignedDate', val)}
                            status={taskValuesErrors?.assignedDate}
                        />
                    ))}
            </div>
            <div className={CnTaskForm('form-block')} onClick={handleDropdownClick}>
                <AppTreeSelect
                    label={'Должны сдать'}
                    treeData={options.dateEndList}
                    placeholder={'Выберите тип сдачи'}
                    size={'large'}
                    onChange={(_, value) => {
                        if (typeof value === 'number') {
                            onTaskFieldSelect('deadlineLesson', value);
                            handleTaskTimeTypeChanged('end', EDeadlineTypes.UNTIL_SELECTED_OL);
                        } else {
                            handleTaskTimeTypeChanged('end', value as EDeadlineTypes);
                        }
                    }}
                />
                {showDeadlineTimeSelector &&
                    (taskValues.deadlineType === EDeadlineTypes.TIME ? (
                        <TaskTimePicker
                            placeholder={'Через'}
                            status={taskValuesErrors?.deadlineDelay}
                            value={taskValues.deadlineDelay}
                            onTimeSelect={(val) => onTaskFieldSelect('deadlineDelay', val)}
                        />
                    ) : (
                        <TaskDatePicker
                            placeholder={'Выберите время'}
                            value={taskValues.deadlineForComplete}
                            disabledDate={(date) => disabledDate(date, interval, periodsData)}
                            disabledTime={(date) => disabledTime(date, taskValues.deadlineForComplete)}
                            handleDateChange={(val) => onTaskFieldSelect('deadlineForComplete', val)}
                            status={taskValuesErrors?.deadlineForComplete}
                        />
                    ))}
            </div>
            <div className={CnTaskForm('form-block')} onClick={handleDropdownClick}>
                <AppSelect
                    label={'Время на выполнение задания'}
                    options={options.timeToComplete}
                    allowClear={false}
                    defaultValue={options.timeToComplete?.find(({ value }) => value === calendarDurationType)}
                    onChange={({ value }) => handleTaskTimeTypeChanged('duration', value as EDurationTypes)}
                />
                {calendarDurationType !== EDurationTypes.UNLIMITED && (
                    <TaskTimePicker
                        placeholder={'Выберите время'}
                        status={taskValuesErrors?.timeToComplete}
                        value={taskValues.timeToComplete}
                        valuePrefix={''}
                        onTimeSelect={(val) => onTaskFieldSelect('timeToComplete', val)}
                    />
                )}
            </div>
            {(isPrincipal || isMethodist || isProctor) && (
                <div className={CnTaskForm('form-block')}>
                    <AppSelect
                        label={'Ответственный преподаватель'}
                        placeholder="Aвтоматически"
                        options={teachersListNormalized}
                        showSearch
                        size={'large'}
                        notFoundContent={'Ничего не найдено'}
                        filterOption={(input: string, option: { value: number; label: string }) =>
                            (String(option?.label).toLowerCase() ?? '').includes(input.toLowerCase())
                        }
                        allowClear
                        onChange={(teacher) => handleTeacherSelect(teacher)}
                    />
                </div>
            )}
        </div>
    );
};
